import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { api } from '../api';
import { useSiteSettings } from '../SiteSettingsContext';
import {
  LayoutDashboard, User, FileText, ShieldCheck, Shield, Settings, Calendar,
  Users, Package, Briefcase, ClipboardCheck, Heart, UtensilsCrossed, Building2,
  Bell, ClipboardList, Lock, LogOut, Stethoscope, Image, Wrench,
  Video, MessageCircle, BookOpen, MessageSquare, Plane, BookUser, RefreshCw
} from 'lucide-react';

// Maps admin section routes to their permission key
const SECTION_PERMISSION = {
  '/admin/inventory-system': 'inventory',
  '/admin/bags':             'bags',
  '/admin/count-session':    'bags',
  '/admin/participants':     'participants',
  '/admin/trips':            'trips',
  '/admin/licenses':         'licenses',
  '/admin/hotels':           'hotels',
  '/admin/reminders':        'reminders',
};

const AppSidebar = ({ isOpen = false, onClose = () => {} }) => {
  const { user, isAdmin, logout, hasPermission } = useAuth();
  const { logoUrl, navIconUrl, orgName, guidePdfUrl } = useSiteSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;
  const navRef = useRef(null);
  const [hasGuardianDependents, setHasGuardianDependents] = useState(false);
  const [isGuardianSession, setIsGuardianSession] = useState(false);

  useEffect(() => {
    if (user) {
      api.get('/auth/me').then(res => {
        if (res.data?.guardian_session) setIsGuardianSession(true);
      }).catch(() => {});
      api.get('/guardian/my-dependents-for-picker').then(res => {
        if (res.data && res.data.length > 0) setHasGuardianDependents(true);
      }).catch(() => {});
    }
  }, [user]);

  // Auto-scroll sidebar to the active link on route change or sidebar open
  useEffect(() => {
    if (!navRef.current) return;
    const timer = setTimeout(() => {
      const activeLink = navRef.current.querySelector('[data-active="true"]');
      if (activeLink) {
        activeLink.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [path, isOpen]);

  const active = (href) =>
    path === href || (href !== '/dashboard' && href !== '/admin' && href !== '/my-trips' && path.startsWith(href));

  const link = (href, icon, label, sub = false) => {
    const Icon = icon;
    const isActive = active(href);

    // Check permissions for admin section links
    const permKey = SECTION_PERMISSION[href];
    const locked = !isAdmin && permKey && !hasPermission(permKey, 'view');

    if (locked) {
      return null; // hide items the user has no access to
    }

    return (
      <Link
        key={href}
        to={href}
        onClick={onClose}
        data-active={isActive || undefined}
        className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-colors
          ${sub ? 'pl-10 py-2.5' : ''}
          ${isActive
            ? 'bg-primary/10 text-primary font-semibold'
            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
          }`}
        data-testid={`nav-${href.replace(/\//g, '-').replace(/^-/, '')}`}
      >
        <Icon className={`flex-shrink-0 ${sub ? 'w-4 h-4' : 'w-5 h-5'}`} />
        {label}
      </Link>
    );
  };

  const divider = (label) => (
    <div key={label} className="pt-3 pb-1">
      <p className="px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">{label}</p>
    </div>
  );

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar panel */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-slate-200
        flex flex-col
        transform transition-transform duration-200 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        {/* Logo — height matches main content header */}
        <div className="px-4 border-b border-slate-100 flex-shrink-0 flex items-center h-[72px]">
          <Link to="/" onClick={onClose} className="flex items-center gap-2.5">
            <div className="w-[68px] h-[68px] rounded-xl overflow-hidden flex-shrink-0 flex items-center justify-center">
              {navIconUrl ? (
                <img src={navIconUrl} alt={orgName} className="w-full h-full object-contain" />
              ) : (
                <Stethoscope className="w-8 h-8 text-primary" />
              )}
            </div>
            <span className="font-heading font-bold text-base text-slate-800 leading-tight">
              {orgName || 'Guatemala Surgery'}
            </span>
          </Link>
        </div>

        {/* Nav items */}
        <nav ref={navRef} className="flex-1 overflow-y-auto p-3 space-y-0.5">
          {/* ── My Section (all logged-in users) ── */}
          {divider('My Account')}
          {link('/dashboard',    LayoutDashboard, 'Dashboard')}
          {link('/my-trips',     Calendar,        'My Trips')}
          {link('/food-fun',     UtensilsCrossed, 'Local Guide')}
          {link('/ask',          MessageCircle,   'Q&A')}
          {link('/profile',      User,            'Profile')}
          {link('/flight-hotel', Plane,           'Flight & Hotel')}
          {link('/waiver',       FileText,        'Waiver')}
          {link('/licenses',     ShieldCheck,     'My Licenses')}
          {guidePdfUrl && (
            <a
              href={guidePdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-colors text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              data-testid="nav-guide"
            >
              <BookOpen className="w-5 h-5 flex-shrink-0" />
              Guide
            </a>
          )}
          {link('/trip-interest', Heart,          'Trip Interest')}

          {/* ── Admin Section ── */}
          {isAdmin && (
            <>
              {divider('Administration')}
              {link('/admin',                   Settings,       'Overview')}
              {link('/admin/trips',             Calendar,       'Trips')}
              {link('/admin/participants',      Users,          'Participants')}
              {link('/admin/inventory-system',  Package,        'Inventory')}
              {link('/admin/bags',              Briefcase,      'Transport Bags', true)}
              {link('/admin/count-session',     ClipboardCheck, 'Inv. Operations',  true)}
              {link('/admin/hotels',            Building2,      'Hotels')}
              {link('/admin/licenses',          ClipboardList,  'License Review')}
              {link('/admin/qa',                MessageSquare,  'Q&A Management')}
              {link('/admin/assignments',       ClipboardList,  'Assignments')}
              {link('/admin/directory',         BookUser,       'Directory')}
              {link('/admin/reminders',         Bell,           'Reminders')}
              {link('/admin/how-to',           Video,          'How-To Guides')}
              {link('/admin/maintenance',      Wrench,         'Maintenance')}
              {link('/admin/permissions',       Lock,           'Permissions')}
              {link('/admin/site-settings',     Image,          'Site Settings')}
              {link('/admin/audit-logs',        Shield,         'Audit Logs')}
            </>
          )}

          {/* ── Partial admin with granted permissions ── */}
          {!isAdmin && user?.role === 'partial_admin' && (
            <>
              {divider('Tools')}
              {link('/admin/inventory-system',  Package,        'Inventory')}
              {link('/admin/bags',              Briefcase,      'Transport Bags', true)}
              {link('/admin/count-session',     ClipboardCheck, 'Inv. Operations',  true)}
              {link('/admin/participants',      Users,          'Participants')}
              {link('/admin/licenses',          ClipboardList,  'License Review')}
              {link('/admin/hotels',            Building2,      'Hotels')}
              {link('/admin/reminders',         Bell,           'Reminders')}
            </>
          )}
        </nav>

        {/* User + Sign Out */}
        {user && (
          <div className="p-4 border-t border-slate-100 flex-shrink-0">
            <div className="flex items-center gap-2 mb-3 px-2">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-4 h-4 text-primary" />
                </div>
              )}
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{user.name || 'User'}</p>
                <p className="text-xs text-slate-400 truncate">{user.email}</p>
              </div>
            </div>
            <button
              onClick={() => { logout(); onClose(); }}
              className="flex items-center gap-2 w-full px-3 py-2 rounded-xl text-sm text-slate-500 hover:text-red-600 hover:bg-red-50 transition-colors"
              data-testid="nav-sign-out"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
            {(hasGuardianDependents || isGuardianSession) && (
              <button
                onClick={async () => {
                  if (isGuardianSession) {
                    try {
                      const { setSessionToken } = await import('../api');
                      const res = await api.post('/guardian/switch-back');
                      if (res.data.token) setSessionToken(res.data.token);
                      window.location.href = '/account-picker';
                    } catch {
                      window.location.href = '/dashboard';
                    }
                  } else {
                    navigate('/account-picker');
                    onClose();
                  }
                }}
                className="flex items-center gap-2 w-full px-3 py-2 rounded-xl text-sm text-purple-600 hover:bg-purple-50 transition-colors mt-1"
                data-testid="nav-switch-account"
              >
                <RefreshCw className="w-4 h-4" />
                Switch Account
              </button>
            )}
          </div>
        )}
      </aside>
    </>
  );
};

export default AppSidebar;
